import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
  Lightbulb,
  Clock,
  User,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/components/ui/utils';
import {
  tipsAtom,
  tipsLoadingAtom,
  tipsErrorAtom,
  updateTipActionAtom,
} from '../stores/tipsStore';
import { tipsService } from '../services/tipsService';
import { TipResponse, TipSource, TipStatus } from '@/types/tips';

interface TipFormData {
  title: string;
  description: string;
  source: TipSource;
  source_url: string;
  source_contact: string;
  priority: number;
  tags: string;
  editorial_notes: string;
}

export function EditTip() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const tips = useAtomValue(tipsAtom);
  const isLoading = useAtomValue(tipsLoadingAtom);
  const error = useAtomValue(tipsErrorAtom);

  const updateTip = useSetAtom(updateTipActionAtom);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tip, setTip] = useState<TipResponse | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<TipFormData>();

  // Find tip from store or fetch it
  useEffect(() => {
    if (!id) return;

    const existingTip = tips.find(t => t.id === id);
    if (existingTip) {
      setTip(existingTip);
      reset({
        title: existingTip.title || '',
        description: existingTip.description || '',
        source: existingTip.source || 'manual',
        source_url: existingTip.sourceUrl || '',
        source_contact: existingTip.sourceContact || '',
        priority: existingTip.priority || 3,
        tags: existingTip.tags || '',
        editorial_notes: existingTip.editorialNotes || '',
      });
    } else {
      // Fetch tip if not in store
      tipsService
        .getTip(id)
        .then(fetchedTip => {
          setTip(fetchedTip);
          reset({
            title: fetchedTip.title || '',
            description: fetchedTip.description || '',
            source: fetchedTip.source || 'manual',
            source_url: fetchedTip.sourceUrl || '',
            source_contact: fetchedTip.sourceContact || '',
            priority: fetchedTip.priority || 3,
            tags: fetchedTip.tags || '',
            editorial_notes: fetchedTip.editorialNotes || '',
          });
        })
        .catch(console.error);
    }
  }, [id, tips, reset]);

  const onSubmit = async (data: TipFormData) => {
    if (!id || !tip) return;

    setIsSubmitting(true);
    try {
      await updateTip(id, {
        title: data.title || null,
        description: data.description || null,
        source_url: data.source_url || null,
        source_contact: data.source_contact || null,
        priority: data.priority || null,
        tags: data.tags || null,
        editorial_notes: data.editorial_notes || null,
      });

      navigate('/inbox');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/inbox');
  };

  const getStatusColor = (status: TipStatus) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 4) return 'bg-red-100 text-red-800';
    if (priority >= 3) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getPriorityLabel = (priority: number) => {
    if (priority >= 4) return 'High';
    if (priority >= 3) return 'Medium';
    return 'Low';
  };

  if (isLoading && !tip) {
    return (
      <div className='h-full flex flex-col'>
        <div className='p-4 border-b'>
          <Skeleton className='h-6 w-48' />
        </div>
        <div className='flex-1 p-4'>
          <div className='space-y-6'>
            <Skeleton className='h-32 w-full' />
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        </div>
      </div>
    );
  }

  if (error || (!isLoading && !tip)) {
    return (
      <div className='h-full flex flex-col'>
        <div className='p-4 border-b'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/inbox'>Tip Inbox</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <Alert variant='destructive' className='max-w-md'>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription>
              {error || 'Tip not found'}
              <div className='mt-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => navigate('/inbox')}
                >
                  Back to Inbox
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!tip) return null;

  return (
    <div className='h-full flex flex-col'>
      {/* Header with Breadcrumbs */}
      <div className='p-4 border-b bg-muted/30'>
        <div className='flex items-center justify-between mb-4'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/inbox'>Tip Inbox</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit Tip</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Button
            variant='outline'
            size='sm'
            onClick={handleCancel}
            className='gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back
          </Button>
        </div>

        <div className='flex items-center gap-3'>
          <h1 className='text-2xl font-bold'>{tip.title}</h1>
          <Badge className={getPriorityColor(tip.priority)}>
            {getPriorityLabel(tip.priority)} Priority
          </Badge>
          <Badge className={getStatusColor(tip.status)}>
            {tip.status.replace('_', ' ')}
          </Badge>
        </div>

        <div className='flex items-center gap-4 mt-2 text-sm text-muted-foreground'>
          <div className='flex items-center gap-1'>
            <Lightbulb className='h-3 w-3' />
            <span>ID: {tip.id.slice(0, 8)}...</span>
          </div>
          <div className='flex items-center gap-1'>
            <User className='h-3 w-3' />
            <span>Source: {tip.source}</span>
          </div>
          <div className='flex items-center gap-1'>
            <Clock className='h-3 w-3' />
            <span>
              Created: {format(new Date(tip.createdAt), 'MMM d, yyyy')}
            </span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className='flex-1 overflow-auto p-4'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='title'>Title *</Label>
                <Input
                  id='title'
                  {...register('title', { required: 'Title is required' })}
                  className={cn(errors.title && 'border-red-500')}
                />
                {errors.title && (
                  <p className='text-sm text-red-500'>{errors.title.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='description'>Description</Label>
                <Textarea
                  id='description'
                  {...register('description')}
                  rows={4}
                  placeholder='Detailed information about the tip...'
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='source'>Source Type</Label>
                  <Select
                    value={watch('source')}
                    onValueChange={(value: TipSource) =>
                      setValue('source', value, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='manual'>Manual</SelectItem>
                      <SelectItem value='email'>Email</SelectItem>
                      <SelectItem value='linkedin'>LinkedIn</SelectItem>
                      <SelectItem value='rss'>RSS Feed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='priority'>Priority (1-5)</Label>
                  <Select
                    value={watch('priority')?.toString()}
                    onValueChange={value =>
                      setValue('priority', Number(value), { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='1'>1 - Low</SelectItem>
                      <SelectItem value='2'>2 - Below Normal</SelectItem>
                      <SelectItem value='3'>3 - Normal</SelectItem>
                      <SelectItem value='4'>4 - High</SelectItem>
                      <SelectItem value='5'>5 - Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='source_url'>Source URL</Label>
                <Input
                  id='source_url'
                  type='url'
                  {...register('source_url')}
                  placeholder='https://example.com/article'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='source_contact'>Source Contact</Label>
                <Input
                  id='source_contact'
                  {...register('source_contact')}
                  placeholder='Contact name or email'
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='tags'>Tags</Label>
                <Input
                  id='tags'
                  {...register('tags')}
                  placeholder='politics, economy, technology (comma-separated)'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='editorial_notes'>Editorial Notes</Label>
                <Textarea
                  id='editorial_notes'
                  {...register('editorial_notes')}
                  rows={3}
                  placeholder='Internal editorial notes...'
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className='flex items-center justify-between pt-4 border-t'>
            <Button
              type='button'
              variant='outline'
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={isSubmitting || !isDirty}
              className='gap-2'
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
